import React from "react";
import { TrustMetric, Challenge, SolutionStep, Result } from "./types";

const CaseStudy: React.FC = () => {
  const trustMetrics: TrustMetric[] = [
    {
      value: "40%",
      label: "Reduction in Time-to-Hire",
      description: "Average time to fill dropped from 45 days to 27 days",
    },
    {
      value: "90%",
      label: "Candidate Completion Rate",
      description:
        "9 out of 10 candidates complete AI interviews within 48 hours",
    },
    {
      value: "10+ hrs",
      label: "Saved Per Recruiter Weekly",
      description: "Automating manual screening reclaims strategic time",
    },
    {
      value: "100%",
      label: "Team Adoption",
      description: "Dedicated training ensured complete platform adoption",
    },
  ];

  const challenges: Challenge = {
    title: "Drowning in Resumes, Struggling to Scale",
    description:
      "A leader in the Financial Technology space, but rapid growth created significant hiring bottlenecks. Their talent acquisition team of 10 recruiters was spending an estimated 60% of their time on manual resume screening and initial phone screens.",
    points: [
      "Slow: It took an average of 15 days just to screen applicants and create a shortlist",
      "Inconsistent: Different recruiters evaluated candidates differently, leading to potential bias",
      "Costly: Recruiters were stuck on administrative tasks instead of engaging high-value candidates",
      "Poor Experience: Candidates were left waiting for weeks, often with no response",
    ],
  };

  const solutionSteps: SolutionStep[] = [
    {
      step: 1,
      title: "Seamless ATS Integration",
      description:
        "The AI Recruiter was connected directly to their Workday instance, allowing recruiters to work from the system they already knew",
    },
    {
      step: 2,
      title: "AI-Powered Screening",
      description:
        "The platform automatically screened and graded incoming candidates for 50+ open roles based on the client's custom criteria",
    },
    {
      step: 3,
      title: "Autonomous AI Interviews",
      description:
        "Top candidates were automatically invited to one-way AI-powered video interviews, which they could complete on their own schedule",
    },
    {
      step: 4,
      title: "Role-Based Training",
      description:
        "The 25th & Staffing team conducted dedicated training sessions for Admins, Recruiters, and Hiring Managers to ensure 100% adoption",
    },
  ];

  const results: Result[] = [
    {
      metric: "40%",
      value: "Reduction in Time-to-Hire",
      description:
        "The average time to fill a role dropped from 45 days to 27 days",
    },
    {
      metric: "10+",
      value: "Hours Saved Per Recruiter, Per Week",
      description:
        "By automating manual screening, the TA team reclaimed over 100 hours of strategic time every week",
    },
    {
      metric: "90%",
      value: "Candidate Completion Rate",
      description:
        "Candidates embraced the flexibility of the AI interviews, with 9 out of 10 completing their interviews within 48 hours",
    },
    {
      metric: "100%",
      value: "Team Satisfaction",
      description:
        "Recruiters could focus on qualified, engaged candidates, and hiring managers received high-quality shortlists in days, not weeks",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-700 text-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
          <div className="text-center">
            <h2 className="text-[20px] md:text-[35px] mb-3 max-w-[800px] mx-auto">
              CASE STUDY: [Client Company Name] Reduces Time-to-Hire by 40% with
              25th AI Recruiter
            </h2>
            <blockquote className="text-xl italic mb-8 max-w-3xl mx-auto">
              "The 25th AI Recruiter transformed our process. We're now filling
              roles 40% faster and our recruiters can focus on what matters:
              building relationships."
            </blockquote>
            <div className="text-lg">
              <p className="">[Client's Name], [Client's Job Title]</p>
              <p>[Client Company Name]</p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Snapshot */}
      <section className="bg-white py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-medium text-gray-900 text-center mb-4">
            Client Snapshot
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div>
                <h3 className=" text-gray-900">Company:</h3>
                <p className="text-gray-600">[Client Company Name]</p>
              </div>
              <div>
                <h3 className=" text-gray-900">Industry:</h3>
                <p className="text-gray-600">Financial Technology</p>
              </div>
              <div>
                <h3 className=" text-gray-900">ATS:</h3>
                <p className="text-gray-600">Workday</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className=" text-gray-900">Company Size:</h3>
                <p className="text-gray-600">500+ Employees</p>
              </div>
              <div>
                <h3 className=" text-gray-900">Challenge:</h3>
                <p className="text-gray-600">
                  High volume of applicants, slow manual screening, inconsistent
                  candidate experience
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Metrics */}
      <section className="bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-medium text-gray-900 text-center mb-4">
            Proven Results & Metrics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {trustMetrics.map((metric, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md p-6 text-center"
              >
                <div className="text-3xl font-medium text-primary-600 mb-2">
                  {metric.value}
                </div>
                <h3 className="text-lg  text-gray-900 mb-2">{metric.label}</h3>
                <p className="text-gray-600 text-sm">{metric.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Challenge */}
      <section className="bg-white py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-medium text-gray-900 text-center mb-8">
            The Challenge
          </h2>
          <div className="bg-red-50 border border-[grey]/20 rounded-lg p-8">
            <h3 className="text-2xl  text-red-800 mb-4">{challenges.title}</h3>
            <p className="text-gray-700 mb-6">{challenges.description}</p>
            <ul className="space-y-3">
              {challenges.points.map((point, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-red-500 mr-3">●</span>
                  <span className="text-gray-700">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* The Solution */}
      <section className="bg-gray-50 py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-medium text-gray-900 text-center mb-4">
            The Solution: An AI Co-pilot for the Entire TA Team
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solutionSteps.map((step) => (
              <div
                key={step.step}
                className="bg-white rounded-lg shadow-md p-6"
              >
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium mr-3">
                    {step.step}
                  </div>
                  <h3 className="text-xl  text-gray-900">{step.title}</h3>
                </div>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Results */}
      <section className="bg-white py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-medium text-gray-900 text-center mb-4">
            The Results: Faster Hiring, Happier Recruiters, Proven ROI
          </h2>
          <div className="text-center mb-5">
            <p className="text-xl text-gray-700">
              Within the first 90 days of going live, [Client Company Name] saw
              transformative results.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {results.map((result, index) => (
              <div key={index} className="border-l-4 border-primary pl-6 py-2">
                <div className="text-2xl font-medium text-primary mb-1">
                  {result.metric}
                </div>
                <h3 className="text-lg  text-gray-900 mb-2">{result.value}</h3>
                <p className="text-gray-600">{result.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-600 py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-medium text-white mb-4">
            Ready to see results like these?
          </h2>
          <p className="text-primary-100 text-xl mb-8">
            Transform your hiring process with 25th AI Recruiter
          </p>
          <button className="bg-primary text-white px-5 py-3 rounded-full font-medium text-lg hover:bg-gray-100 transition-colors duration-200">
            Request Your Demo Today
          </button>
        </div>
      </section>
    </div>
  );
};

export default CaseStudy;
